import axios from 'axios';
import config from '../../../config';

// ─── Dedicated axios instance ────────────────────────────────────────────────
// Uses the token from the supplements-order Context. The token is injected via
// the `getToken` parameter pattern — callers pass the current token from context.
// All supplements-order APIs route through this single instance.
const apiInstance = axios.create({
  baseURL: config.pharmaServer,
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FALLBACK_PINCODE = '560001';

// Dedicated sessionStorage key for this flow's selected delivery address.
// Kept separate from lab-test's `selected_address` and pharmacy's
// `pharma_selectedAddress` so the flows never clash in the same session.
export const SUPPLEMENTS_ADDRESS_KEY = 'supplements_selected_address';

/**
 * Get pincode from the flow's selected address in sessionStorage.
 */
function getActivePincode() {
  try {
    const stored = sessionStorage.getItem(SUPPLEMENTS_ADDRESS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.pincode) return String(parsed.pincode);
    }
  } catch (e) {
    // ignore parse errors
  }
  return FALLBACK_PINCODE;
}

/**
 * Build auth headers from token.
 */
function authHeaders(token) {
  return { authorization: token };
}

// ─── Redeem one-time token (OTT) ─────────────────────────────────────────────

/**
 * Exchange a one-time token (from the deeplink `userParam`) for a JWT.
 * Endpoint: POST /new-auth/redeem-token  body: { ott }
 *
 * @param {string} ott - The one-time token from the deeplink
 * @returns {Promise<{ message: string, jwt: string, flow: string, userId: number }>}
 */
export async function redeemToken(ott) {
  const res = await apiInstance.post(
    '/new-auth/redeem-token',
    { ott },
    {
      baseURL: config.apiBaseUrl,
      headers: { 'Content-Type': 'application/json' },
    }
  );
  return res.data;
}

/**
 * `invalid_reason` values for the `retail_autocart_link_opened` analytics event.
 *
 * NOTE: the spec also defines `already_ordered`, which the backend cannot
 * currently be distinguished from `EXPIRED` — see getOttInvalidReason below.
 */
export const OTT_INVALID_REASON = {
  EXPIRED: 'expired',
  AUTH_FAILED: 'auth_failed',
};

/**
 * Map a failed `redeemToken` call to an `invalid_reason` for analytics.
 *
 * Observed responses from /new-auth/redeem-token:
 *   400 { message: 'Invalid fields',  errorMessage: 'ott- ott must be 32 lowercase hex…' }
 *   401 { message: 'serverError',     errorMessage: 'Session has expired' }
 *
 * We key off the HTTP status rather than `errorMessage`, because that string is
 * human-readable copy with no stability guarantee — matching on it would
 * silently start misreporting the day someone rewords it.
 *
 * Caveat: the 401 bucket covers an expired OTT, an already-consumed OTT and one
 * that never existed. They are indistinguishable over the wire, so a link that
 * was already used to place an order reports as `expired`, not
 * `already_ordered`. Separating them needs a distinct error code from backend.
 *
 * @param {Error} err - The error thrown by redeemToken (axios error)
 * @returns {string} One of OTT_INVALID_REASON
 */
export function getOttInvalidReason(err) {
  return err?.response?.status === 401
    ? OTT_INVALID_REASON.EXPIRED
    : OTT_INVALID_REASON.AUTH_FAILED;
}

// ─── Cart item origin ────────────────────────────────────────────────────────

/**
 * Item origin values: whether a cart line came from the prescription that
 * seeded the auto-cart, or was added by the user from the recommendations rail.
 */
export const ITEM_ORIGIN = {
  PRESCRIBED: 'prescribed',
  RECOMMENDED: 'recommended',
};

/**
 * Resolve a cart line's origin from the raw API item.
 *
 * The rxCart detail response is not currently known to carry this. Several
 * plausible shapes are accepted so the field starts working as soon as backend
 * adds it under any of them:
 *   itemSource / source: 'prescribed' | 'recommended' (or 'prescription' / 'recommendation')
 *   isPrescribed:        boolean or 0/1
 *
 * Returns undefined when the response says nothing, which is the signal for
 * analytics to fall back to its in-session tracking of rail adds.
 *
 * @param {Object} item - Raw cartItem from the API
 * @returns {string|undefined} One of ITEM_ORIGIN, or undefined when unknown
 */
function resolveItemOrigin(item) {
  const raw = item?.itemSource ?? item?.source;
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized.startsWith('prescri')) return ITEM_ORIGIN.PRESCRIBED;
    if (normalized.startsWith('recommend')) return ITEM_ORIGIN.RECOMMENDED;
  }

  const flag = item?.isPrescribed;
  if (flag === true || flag === 1) return ITEM_ORIGIN.PRESCRIBED;
  if (flag === false || flag === 0) return ITEM_ORIGIN.RECOMMENDED;

  return undefined;
}

// ─── Cart API response mapper ────────────────────────────────────────────────

/**
 * Maps the /search-and-book/rxCart/detail response into the shape our
 * Context/UI use.
 *
 * API shape (cartDetail):
 *  - cartItems[]: { cartItemId, dimDrugId, partnerItemCode, brandName,
 *      packSize, quantity, minQuantity, maxQuantity, mrp, cost,
 *      discountedPrice, drugImageUrl, label, isAvailable }
 *      Note: mrp/discountedPrice are PER-UNIT; cost is the line total.
 *  - Pricing: totalMarkedPrice (subtotal), totalDiscount, deliveryCharge,
 *      additionalCharges.{deliveryCharge,handlingCharge}, totalAmount, userPayable
 *  - Address: addressId + addressDetail{ latitude, longitude, address,
 *      pincode, city, state }
 *  - cashbackPercentage, cashbackAmount, estimatedDelivery, isCodOrder
 */
function mapCartResponse(data) {
  const cart = data.cartDetail || data.cart || data;

  const items = (cart.cartItems || cart.items || []).map((item) => {
    const mrp = item.mrp ?? 0;
    const price = item.discountedPrice ?? item.mrp ?? 0;
    const discountPercent =
      mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
    return {
      cartItemId: String(item.cartItemId),
      dimDrugId: item.dimDrugId,
      productId: item.partnerItemCode,
      name: item.brandName || item.name || '',
      packSize: item.label || undefined,
      image: item.drugImageUrl || undefined,
      price,
      mrp,
      discountPercent,
      quantity: item.quantity,
      minQuantity: item.minQuantity || 1,
      maxQuantity: item.maxQuantity || 10,
      isAvailable: item.isAvailable === 1 || item.isAvailable === true,
      isRxRequired: item.rxRequired === 1,
      // Where the line came from: the teleconsult prescription that seeded the
      // auto-cart, vs. the user adding it from the recommendations rail.
      //
      // NOT confirmed to exist on the response — mapped optimistically (same
      // approach as `addressDetail.title`) so the analytics split becomes exact
      // the moment backend sends it, with no further FE change. Stays undefined
      // until then, and analytics falls back to tracking rail adds in-session.
      // See resolveItemOrigin below for the accepted shapes.
      origin: resolveItemOrigin(item),
    };
  });

  const additional = cart.additionalCharges || {};
  const delivery = additional.deliveryCharge ?? cart.deliveryCharge ?? 0;
  const handling = additional.handlingCharge ?? cart.handlingCharge ?? 0;

  const addressDetail = cart.addressDetail || null;

  // estimatedDelivery is an object { etaFrom, etaTo, tat, ... } whose `tat`
  // is the ready-to-display string ("Delivery by tomorrow – 5 Sept").
  // estimatedDeliveryTat carries the same string at the top level.
  const estimatedDeliveryText =
    cart.estimatedDelivery?.tat ||
    cart.estimatedDeliveryTat ||
    (typeof cart.estimatedDelivery === 'string'
      ? cart.estimatedDelivery
      : undefined);

  return {
    cartId: String(cart.cartId || cart.id || ''),
    items,
    priceBreakdown: {
      subtotal: cart.totalMarkedPrice ?? cart.totalMrp ?? 0,
      discount: cart.totalDiscount ?? 0,
      delivery,
      handling,
      total: cart.userPayable ?? cart.totalAmount ?? 0,
    },
    estimatedDelivery: estimatedDeliveryText,
    userDetails: data.userDetails
      ? {
          userName: data.userDetails.userName || '',
          phone: String(data.userDetails.phone || ''),
        }
      : undefined,
    address:
      cart.addressId && addressDetail
        ? {
            addressId: String(cart.addressId),
            pincode: String(addressDetail.pincode || ''),
            addressLine: addressDetail.address || '',
            displayName: addressDetail.city || '',
            locality: addressDetail.city || '',
            city: addressDetail.city || '',
            state: addressDetail.state || '',
            lat: addressDetail.latitude,
            long: addressDetail.longitude,
            // The user-facing label ("Home" / "Work" / ...). The saved-addresses
            // API (GET /cart/addresses) returns this as `title` — see
            // AddressSelect/AddressCard — but it is NOT confirmed to be present on
            // the rxCart detail's `addressDetail`. Mapped optimistically with a
            // fallback chain so it starts populating the moment backend sends it;
            // stays undefined until then (analytics falls back to `city`).
            title: addressDetail.title || addressDetail.label || undefined,
          }
        : undefined,
    // Cashback comes as amount + percentage at the cartDetail level.
    cashback:
      cart.cashbackAmount != null
        ? {
            amount: cart.cashbackAmount,
            percentage: cart.cashbackPercentage,
          }
        : null,
    // Minimum cart value (userPayable) required to unlock free delivery.
    // Backend sends this as `minAmountRequiredForFreeDelivery` on the rxCart
    // detail response.
    freeDeliveryThreshold:
      cart.minAmountRequiredForFreeDelivery ??
      data.minAmountRequiredForFreeDelivery ??
      data.freeDeliveryThreshold ??
      cart.freeDeliveryThreshold ??
      null,
    // Absent means COD. Only an explicit `false` makes a cart prepaid, which
    // matches the pharmacy flow's `codFlag !== false` convention and keeps a
    // response that omits the field on the behaviour this flow has always had.
    isCodOrder: cart.isCodOrder ?? true,
    // Delivery partner logo (e.g. 1mg) shown on the delivery card.
    partnerLogo: cart.partnerLogo || null,
    // Brand footer logos: visithealthLogo (left/VISIT side) + sponsorLogo (right).
    visithealthLogo: cart.visithealthLogo || null,
    sponsorLogo: cart.sponsorLogo || null,
  };
}

// ─── Fetch Cart ──────────────────────────────────────────────────────────────

/**
 * Fetch the current cart detail.
 * Endpoint: GET /search-and-book/cart/detail/current
 * The cart is resolved from the authenticated user's token (which carries the
 * cartId), so no query params are needed.
 *
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Mapped cart response
 */
export async function fetchCart(token) {
  const res = await apiInstance.get('/search-and-book/rxCart/detail', {
    headers: authHeaders(token),
  });

  return mapCartResponse(res.data);
}

// ─── Update Cart Item ────────────────────────────────────────────────────────

/**
 * Modify item quantity in the cart.
 *
 * Mirrors the pharmacy search-and-book flow (see MedicineSBCart /
 * updateMedicineCartItems): the cart item is keyed by `cartItemId` and the
 * quantity is ABSOLUTE (the backend does `SET quantity = ?`, not an
 * increment). Sending `quantity: 0` soft-deletes the line.
 *
 * The update endpoint returns only `{ success }` — it does NOT echo the cart —
 * so we refetch the cart detail afterwards to get the authoritative state.
 * Using cartItemId + absolute quantity + refetch is what prevents the stale
 * re-render / duplicate-item drift that the old wellness-cart modify path had.
 *
 * Endpoint: PATCH /search-and-book/cart/update  body: { cartItemId, quantity }
 *
 * @param {string} token - Auth token
 * @param {string|number} cartItemId - Cart line item identifier
 * @param {number} quantity - New absolute quantity (0 removes the item)
 * @returns {Promise<Object>} Mapped (refetched) cart response
 */
export async function updateCartItem(token, cartItemId, quantity) {
  await apiInstance.patch(
    '/search-and-book/cart/update',
    { cartItemId: Number(cartItemId), quantity: Number(quantity) },
    { headers: authHeaders(token) }
  );

  // update returns no cart body — refetch the authoritative cart detail.
  return fetchCart(token);
}

// ─── Remove Cart Item ────────────────────────────────────────────────────────

/**
 * Delete an item from the cart.
 *
 * Same endpoint as updateCartItem — a removal is just an update to quantity 0
 * (matches the search-and-book backend, which has no separate delete route).
 *
 * Endpoint: PATCH /search-and-book/cart/update  body: { cartItemId, quantity: 0 }
 *
 * @param {string} token - Auth token
 * @param {string|number} cartItemId - Cart line item identifier
 * @returns {Promise<Object>} Mapped (refetched) cart response
 */
export async function removeCartItem(token, cartItemId) {
  return updateCartItem(token, cartItemId, 0);
}

// ─── Add to Cart ─────────────────────────────────────────────────────────────

/**
 * Add a product to the cart (used by the recommended/frequently-bought rail).
 *
 * Mirrors the pharmacy search-and-book add flow (addMedicineToCart): posts to
 * the search-and-book service on the same base as the rest of the cart APIs.
 * The add endpoint keys the upsert by `dimDrugId` and returns only
 * `{ cartId, cartItemId }` — NOT the cart — so we refetch the authoritative
 * cart detail afterwards (same pattern as updateCartItem).
 *
 * Endpoint: POST /search-and-book/cart/add
 * Payload:  { cartItem: { dimDrugId, quantity }, cartId?, addressId? }
 *
 * @param {string} token - Auth token
 * @param {Object} data - { dimDrugId, quantity, cartId, addressId }
 * @returns {Promise<Object>} Mapped (refetched) cart response
 */
export async function addToCart(
  token,
  { dimDrugId, quantity = 1, cartId, addressId }
) {
  const body = {
    cartItem: { dimDrugId: Number(dimDrugId), quantity },
  };
  if (cartId) body.cartId = Number(cartId);
  if (addressId) body.addressId = Number(addressId);

  await apiInstance.post('/search-and-book/cart/add', body, {
    headers: authHeaders(token),
  });

  // add returns no cart body — refetch the authoritative cart detail.
  return fetchCart(token);
}

// ─── Fetch Recommended Products ──────────────────────────────────────────────

/**
 * Fetch frequently bought / recommended products.
 * @param {string} token - Auth token
 * @param {string} cartId - Current cart ID
 * @param {number} [limit=10] - Max products to return
 * @returns {Promise<Array>} List of recommended product objects
 */
export async function fetchRecommendedProducts(token, cartId, limit = 10) {
  const pincode = getActivePincode();
  const params = { limit, pincode, cartId };

  const res = await apiInstance.get('/smart-report/recommended-products', {
    params,
    headers: authHeaders(token),
  });

  const data = res.data;
  const drugs = data.drugs || [];

  return drugs.map((drug) => ({
    id: drug.partnerItemCode,
    dimDrugId: drug.dimDrugId,
    partnerItemCode: drug.partnerItemCode,
    name: drug.brandName || '',
    description: drug.description || undefined,
    packSize: drug.label || undefined,
    image: drug.drugImageUrl || undefined,
    price: drug.discountedPrice ?? drug.mrp ?? 0,
    mrp: drug.mrp ?? 0,
    discountPercent:
      drug.mrp && drug.discountedPrice && drug.mrp > drug.discountedPrice
        ? Math.round(((drug.mrp - drug.discountedPrice) / drug.mrp) * 100)
        : 0,
    isAvailable: drug.isAvailable === 1 || drug.isAvailable === true,
    minQuantity: drug.minQuantity || 1,
    maxQuantity: drug.maxQuantity || 10,
    isAdded: drug.isAdded ?? false,
  }));
}

// ─── Update Cart Address ─────────────────────────────────────────────────────

/**
 * Attach an address to the wellness cart.
 * @param {string} token - Auth token
 * @param {string} cartId - Cart ID
 * @param {string} addressId - Address ID
 * @returns {Promise<Object>} Mapped cart response
 */
export async function updateCartAddress(token, cartId, addressId) {
  const res = await apiInstance.patch(
    '/smart-report/cart/address',
    { cartId, addressId },
    {
      params: { pincode: getActivePincode() },
      headers: authHeaders(token),
    }
  );

  return mapCartResponse(res.data);
}

// ─── Address services (medicine flow) ────────────────────────────────────────
// These mirror the pharmacy/medicine-delivery address APIs. Most route through
// apiInstance's default base (config.pharmaServer); the two
// /medicine-delivery/v2/addresses calls override baseURL to config.apiBaseUrl
// (NewBackend), same as redeemToken. Endpoints match the reference repo:
//   GET  /cart/addresses                       -> saved addresses list        (pharmaServer)
//   POST /medicine-delivery/v2/addresses       -> create new address          (apiBaseUrl)
//   POST /labs-v2/address/edit                 -> edit existing address        (apiBaseUrl)
//   POST /medicine-delivery/v2/addresses/remove-> delete address              (apiBaseUrl)
//   POST /cart/addresses/add                    -> attach address to cart      (pharmaServer)

/**
 * Fetch the saved delivery addresses for the medicine cart.
 * @param {string} token - Auth token
 * @returns {Promise<Array>} List of saved address objects
 */
export async function fetchCartAddresses(token) {
  const res = await apiInstance.get('/cart/addresses', {
    headers: authHeaders(token),
  });
  return res.data?.addresses || [];
}

/**
 * Create a new address.
 * @param {string} token - Auth token
 * @param {Object} addressDetails - { title, address, locality, city, pincode, lat, lng }
 * @returns {Promise<Object>} API response (may include address/addressId)
 */
export async function saveNewAddress(token, addressDetails) {
  const res = await apiInstance.post(
    '/medicine-delivery/v2/addresses',
    addressDetails,
    { baseURL: config.apiBaseUrl, headers: authHeaders(token) }
  );
  return res.data;
}

/**
 * Edit an existing address (same-pincode updates only, per backend).
 * @param {string} token - Auth token
 * @param {Object} addressDetails - includes addressId
 * @returns {Promise<Object>} API response
 */
export async function editAddress(token, addressDetails) {
  const res = await apiInstance.post('/labs-v2/address/edit', addressDetails, {
    baseURL: config.apiBaseUrl,
    headers: authHeaders(token),
  });
  return res.data;
}

/**
 * Delete an address.
 * @param {string} token - Auth token
 * @param {string|number} addressId - Address to remove
 * @returns {Promise<Object>} API response
 */
export async function removeAddress(token, addressId) {
  const res = await apiInstance.post(
    '/medicine-delivery/v2/addresses/remove',
    { addressId },
    { baseURL: config.apiBaseUrl, headers: authHeaders(token) }
  );
  return res.data;
}

/**
 * Attach an address to the current cart (medicine flow).
 * @param {string} token - Auth token
 * @param {string} cartId - Cart ID
 * @param {string|number} addressId - Address to attach
 * @returns {Promise<Object>} API response
 */
export async function addAddressToCart(token, cartId, addressId) {
  const res = await apiInstance.post(
    '/cart/addresses/add',
    { cartId, addressId },
    { headers: authHeaders(token) }
  );
  return res.data;
}

// ─── Place Order ─────────────────────────────────────────────────────────────

/**
 * Strips the "JWT " / "Bearer " prefix from a token, since the place-order
 * endpoint expects the raw JWT in the `auth` query param.
 */
function rawToken(token) {
  if (!token) return '';
  return token.replace(/^(JWT|Bearer)\s+/i, '').trim();
}

/**
 * Build the prepaid (non-COD) place-order URL.
 *
 * Endpoint: GET {pharmaServer}/search-and-book/supplement-order/place
 * Query:    cartId, auth, [addressId], [source]
 * Returns:  200 text/html — a streamed Cashfree checkout page.
 *
 * The response is a self-contained HTML payment page that must be *rendered
 * and navigated* by the browser — not fetched as data. That's why we build a
 * URL string to navigate to instead of calling apiInstance.get: an XHR would
 * only give us the HTML as a string, and the auto-submitting payment form /
 * gateway redirect wouldn't run.
 *
 * Auth: the endpoint accepts `Authorization: JWT <token>`, but a browser
 * navigation can't attach a header, so the raw JWT (no prefix) goes in the
 * `auth` query param instead.
 *
 * Reuses apiInstance.defaults.baseURL (config.pharmaServer) so the base isn't
 * duplicated.
 *
 * @param {string} token - Auth token (with or without "JWT " prefix)
 * @param {Object} data - { cartId, addressId, source }
 * @returns {string} The full checkout URL to navigate the browser to
 */
export function getPlaceOrderUrl(token, { cartId, addressId, source }) {
  const params = new URLSearchParams();
  params.set('cartId', String(cartId));
  params.set('auth', rawToken(token));
  if (addressId) params.set('addressId', String(addressId));
  if (source) params.set('source', source);

  const base = apiInstance.defaults.baseURL;
  return `${base}/search-and-book/supplement-order/place?${params.toString()}`;
}

/**
 * Place a Cash-on-Delivery order. Called from the COD confirmation bottom
 * sheet. Returns an orderId used by the success page's track API.
 *
 * Endpoint: POST {pharmaServer}/search-and-book/order/place
 * Payload:  { cartId: <number>, addressId: <number> }
 *
 * @param {string} token - Auth token
 * @param {Object} data - { cartId, addressId }
 * @returns {Promise<{ orderId: string|number, raw: Object }>}
 */
export async function placeCodOrder(token, { cartId, addressId }) {
  const body = { cartId: Number(cartId) };
  if (addressId) body.addressId = Number(addressId);

  const res = await apiInstance.post('/search-and-book/order/place', body, {
    headers: authHeaders(token),
  });

  const data = res.data || {};
  // Normalize the orderId from common response shapes.
  const orderId =
    data.orderId ??
    data.order?.orderId ??
    data.orderBasicDetail?.orderId ??
    data.orderTrackDetail?.orderBasicDetail?.orderId ??
    cartId;
  return { orderId, raw: data };
}

// ─── Fetch Order Status ──────────────────────────────────────────────────────

/**
 * Fetch the status of a placed supplement order (after payment).
 * @param {string} token - Auth token
 * @param {string} orderId - Order ID from URL params
 * @returns {Promise<Object>} Order track detail
 */
export async function fetchOrderStatus(token, orderId) {
  const res = await apiInstance.get('/search-and-book/order/track', {
    params: { orderId },
    headers: authHeaders(token),
  });

  const data = res.data || {};
  const track = data.orderTrackDetail || data;
  const basic = track.orderBasicDetail || {};

  return {
    orderBasicDetail: basic,
    orderItems: track.orderItems || [],
    statusTimeLine: track.statusTimeLine || [],
    disclaimers: track.disclaimers || [],
    note: track.note || '',
    isCancellable: track.isCancellable || false,
    // Brand-footer logos — the track response carries them (falls back across
    // the common locations). Used to re-populate the footer after a refresh,
    // when the cart Context is empty.
    visithealthLogo:
      track.visithealthLogo ||
      basic.visithealthLogo ||
      data.visithealthLogo ||
      null,
    sponsorLogo:
      track.sponsorLogo || basic.sponsorLogo || data.sponsorLogo || null,
    partnerLogo:
      track.partnerLogo || basic.partnerLogo || basic.partnerLogoUrl || null,
  };
}

export async function redeemTokenV2(ott) {
  const res = await apiInstance.post(
    'new-auth/redeem-token-v2',
    { ott },
    {
      baseURL: config.apiBaseUrl,
      headers: { 'Content-Type': 'application/json' },
    }
  );
  // The JWT comes back in the `authorization` response header; the body
  // carries the `flow` that decides which upsell screen to land on, plus
  // flow-specific `data` (e.g. `verticalId` for doctorRecommendation).
  return {
    authToken: res.headers.authorization,
    flow: res.data?.flow,
    data: res.data?.data || {},
  };
}
